import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@/lib/api';
import { autosaveComposition } from '@/data/sources-social';
import type { VisualBrief } from '@/types/social';

/* ----------------------------------------------------------------------------
 * P0-A Studio autosave + refresh-recovery hook (canva-audit-5).
 *
 * - Debounced (~2s) autosave of the editable composition via the SAFE,
 *   approval-preserving backend path (autosaveComposition -> composition_only).
 *   An APPROVED draft is NEVER re-opened to draft by an autosave.
 * - Immediate localStorage mirror on every edit, so a refresh / navigation /
 *   interruption can offer "Restore unsaved changes?".
 * - Optimistic concurrency: the server rejects a stale write (409); the hook
 *   surfaces a `conflict` so the editor can prompt "reload the newer version"
 *   and stops autosaving until the caller reloads (never clobbers newer state).
 * - Never overwrites the row's workspace/domain/project context (the backend
 *   composition-only save leaves all of that untouched).
 * -------------------------------------------------------------------------- */

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict';

/** The subset of editor state autosave persists (composition only). */
export interface StudioEditableState {
  title: string;
  bodyText: string;
  layoutKey: string;
  visualBrief: VisualBrief | null;
}

interface StoredDraft {
  contentId: number;
  savedAt: string;
  baseUpdatedAt: string | null;
  state: StudioEditableState;
}

const LS_PREFIX = 'bc:studio:autosave:';
const lsKey = (id: number) => `${LS_PREFIX}${id}`;

function readStored(id: number): StoredDraft | null {
  try {
    const raw = window.localStorage.getItem(lsKey(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    return parsed && parsed.contentId === id && parsed.state ? parsed : null;
  } catch {
    return null;
  }
}
function writeStored(d: StoredDraft): void {
  try {
    window.localStorage.setItem(lsKey(d.contentId), JSON.stringify(d));
  } catch {
    /* quota / disabled storage — non-fatal, autosave still hits the server */
  }
}
function clearStored(id: number): void {
  try {
    window.localStorage.removeItem(lsKey(id));
  } catch {
    /* non-fatal */
  }
}

export interface UseStudioAutosaveArgs {
  contentId: number;
  /** false while the item is still loading / invalid — no autosave, no recovery. */
  enabled: boolean;
  /** current editable state (recomputed each render; compared by value). */
  state: StudioEditableState;
  /** the row's server updated_at at load / after an external refresh. */
  serverUpdatedAt: string | null;
  debounceMs?: number;
}

export interface UseStudioAutosave {
  status: AutosaveStatus;
  lastSavedAt: number | null;
  conflict: boolean;
  errorMessage: string | null;
  /** flush a pending autosave immediately (e.g. before navigating away). */
  saveNow: () => void;
  /** a locally-stored draft that differs from the loaded server state, or null. */
  recoverable: StudioEditableState | null;
  /** apply + consume the stored draft (returns it so the editor can hydrate). */
  restore: () => StudioEditableState | null;
  /** discard the stored draft + dismiss the recovery banner. */
  dismissRecovery: () => void;
}

export function useStudioAutosave({
  contentId,
  enabled,
  state,
  serverUpdatedAt,
  debounceMs = 2000,
}: UseStudioAutosaveArgs): UseStudioAutosave {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [conflict, setConflict] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [recoverable, setRecoverable] = useState<StudioEditableState | null>(null);

  // Concurrency baseline — advances after each successful save; re-synced when
  // the server row is externally refreshed (manual Save / refetch).
  const baseUpdatedAt = useRef<string | null>(serverUpdatedAt);
  // The serialized state we last persisted (or the primed server state), so we
  // never autosave a no-op or fire immediately on load.
  const lastSerialized = useRef<string>('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const primed = useRef(false);

  const serialized = useMemo(() => JSON.stringify(state), [state]);

  // Prime ONCE when the item has loaded (enabled true): capture the server state
  // as the autosave/recovery baseline, and offer recovery if a locally-stored
  // draft differs from what the server just returned.
  useEffect(() => {
    if (!enabled || primed.current) return;
    primed.current = true;
    lastSerialized.current = serialized;
    baseUpdatedAt.current = serverUpdatedAt;
    const stored = readStored(contentId);
    if (stored && JSON.stringify(stored.state) !== serialized) {
      setRecoverable(stored.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, contentId]);

  // Keep the baseline current when the server row is refreshed externally
  // (manual Save invalidates the query -> new updated_at -> clears any conflict).
  useEffect(() => {
    if (!primed.current) return;
    baseUpdatedAt.current = serverUpdatedAt;
    if (serverUpdatedAt) setConflict((c) => (c ? false : c));
  }, [serverUpdatedAt]);

  const doSave = useCallback(
    async (snapshot: StudioEditableState, snapSerialized: string) => {
      setStatus('saving');
      setErrorMessage(null);
      try {
        const updated = await autosaveComposition(contentId, {
          title: snapshot.title,
          bodyText: snapshot.bodyText,
          layoutKey: snapshot.layoutKey,
          visualBrief: snapshot.visualBrief,
          baseUpdatedAt: baseUpdatedAt.current,
        });
        baseUpdatedAt.current = updated.updatedAt ?? baseUpdatedAt.current;
        lastSerialized.current = snapSerialized;
        clearStored(contentId); // saved to the server — nothing left to recover.
        setStatus('saved');
        setLastSavedAt(Date.now());
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          // Never overwrite a newer revision — lock autosave until reload.
          setConflict(true);
          setStatus('conflict');
        } else {
          setStatus('error');
          setErrorMessage(
            err instanceof ApiError ? err.message : 'Could not save. Your changes are kept locally.',
          );
        }
      }
    },
    [contentId],
  );

  // Debounced autosave + immediate localStorage mirror on every real change.
  useEffect(() => {
    if (!enabled || !primed.current || conflict) return;
    if (serialized === lastSerialized.current) return;

    // Immediate refresh-recovery mirror (not debounced).
    writeStored({
      contentId,
      savedAt: new Date().toISOString(),
      baseUpdatedAt: baseUpdatedAt.current,
      state,
    });

    if (timer.current) clearTimeout(timer.current);
    const snapshot = state;
    const snapSerialized = serialized;
    timer.current = setTimeout(() => {
      void doSave(snapshot, snapSerialized);
    }, debounceMs);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, enabled, conflict, contentId, debounceMs]);

  const saveNow = useCallback(() => {
    if (!enabled || conflict) return;
    if (serialized === lastSerialized.current) return;
    if (timer.current) clearTimeout(timer.current);
    void doSave(state, serialized);
  }, [enabled, conflict, serialized, state, doSave]);

  const restore = useCallback((): StudioEditableState | null => {
    const stored = readStored(contentId);
    setRecoverable(null);
    return stored?.state ?? null;
  }, [contentId]);

  const dismissRecovery = useCallback(() => {
    clearStored(contentId);
    setRecoverable(null);
  }, [contentId]);

  return { status, lastSavedAt, conflict, errorMessage, saveNow, recoverable, restore, dismissRecovery };
}
